import React, { useState, useRef } from 'react';
import StyledQuill from './shared/StyledQuill';
import { useNavigate } from 'react-router-dom';
import { updateProject } from '../utils/projectHandlers';
import { useAuth } from '../contexts/AuthContext';
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalInnerContent,
    HeaderButton,
    ProjectTitle,
    ProjectTagline,
    ProjectUrl,
    ProjectDescription,
    SaveButton
} from '../styles/ProjectDetailsModal.styles';
import CloseIcon from './icons/CloseIcon';
import EditIcon from './icons/EditIcon';
import { ViewIcon } from './icons/ViewIcon';
import { makeLinksClickable, getContrastYIQ } from '../utils/textUtils';

const ProjectDetailsModal = ({ project, onClose }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const textColor = project.background?.color && !project.background?.urls?.thumb ? getContrastYIQ(project.background.color) : null;
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [description, setDescription] = useState(project.description || '');
    const quillRef = useRef(null);

    const handleOpenProject = () => {
        navigate(`/project/${project.id}`);
        onClose();
    };

    const handleSaveDescription = async () => {
        try {
            await updateProject(project.id, { description });
            setIsEditingDescription(false);
        } catch (error) {
            console.error('Error saving description:', error);
        }
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()} background={project.background?.urls?.thumb} backgroundColor={project.background?.color}>
                <ModalHeader textColor={textColor}>
                    <h2>{project.title}</h2>
                    <div>
                        <HeaderButton onClick={handleOpenProject} title="Open Project Board" textColor={textColor}>
                            <ViewIcon />
                        </HeaderButton>
                        <HeaderButton onClick={() => setIsEditingDescription(!isEditingDescription)} title="Edit Description" textColor={textColor}>
                            <EditIcon />
                        </HeaderButton>
                        <HeaderButton onClick={onClose} title="Close" textColor={textColor}>
                            <CloseIcon />
                        </HeaderButton>
                    </div>
                </ModalHeader>
                <ModalInnerContent>
                    {project.tagline && (
                        <ProjectTagline textColor={textColor}>
                            {makeLinksClickable(project.tagline)}
                        </ProjectTagline>
                    )}
                    {project.url && (
                        <ProjectUrl href={project.url} target="_blank" rel="noopener noreferrer" textColor={textColor}>
                            {project.url}
                        </ProjectUrl>
                    )}
                    <ProjectDescription textColor={textColor}>
                        {isEditingDescription ? (
                            <StyledQuill
                                ref={quillRef}
                                theme="snow"
                                value={description}
                                onChange={setDescription}
                                modules={{
                                    toolbar: [
                                        ['bold', 'italic', 'underline', 'strike'],
                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                        ['link', 'image']
                                    ],
                                    clipboard: {
                                        matchVisual: false,
                                    },
                                }}
                                formats={[
                                    'bold', 'italic', 'underline', 'strike',
                                    'list', 'bullet',
                                    'link', 'image'
                                ]}
                                placeholder="Add a detailed description..."
                            />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: description || 'No description provided.' }} />
                        )}
                    </ProjectDescription>
                    {isEditingDescription && (
                        <SaveButton onClick={handleSaveDescription}>Save Description</SaveButton>
                    )}
                </ModalInnerContent>
            </ModalContent>
        </ModalOverlay>
    );
};

export default ProjectDetailsModal;